function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { all, call, put, takeEvery, takeLatest, take, delay, select } from 'redux-saga/effects';
import queryString from 'query-string';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import minBy from 'lodash/minBy';
import find from 'lodash/find';
import maxBy from 'lodash/maxBy';
import get from 'lodash/get';
import AlertService from 'services/AlertService';
import NotificationsService from 'services/NotificationsService';
import ApiService from 'services/ApiService';
import App from 'modules/App';
import * as appActionTypes from 'modules/App/actionTypes';
import Account from 'modules/Account';
import * as HcpSelectors from 'modules/Hcp/selectors';
import * as HcpActionTypes from 'modules/Hcp/actionTypes';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import * as selectors from './selectors';
import * as constants from './constants';
import { transformNotificationsRecord } from './helpers';
function* mapNotificationResourcesToLinks(notificationsRecords) {
    const localizationResources = yield select(App.selectors.localizationResources);
    const resourceKeys = [
        ...new Set(notificationsRecords.map((notificationsRecord)=>notificationsRecord.resourceKey))
    ];
    const resources = resourceKeys.map((key)=>localizationResources[key]);
    const result = Object.fromEntries(resources.map(({ resourceKey, value })=>[
            resourceKey,
            Object.values(constants.NOTIFICATION_LINKS).filter((link)=>value.includes(`{%% link ${link} %%}`))
        ]));
    return result;
}
function* fetchList() {
    const alert = yield call(AlertService.createAlertDispatcher, {
        isGlobal: true
    });
    try {
        const notificationsFilter = yield select(selectors.notificationsFilter);
        const activeOrganizationUID = yield select(Account.selectors.activeOrganizationUID);
        const activeProfileType = yield select(Account.selectors.activeProfileType);
        const query = queryString.stringify(omitBy(_object_spread_props(_object_spread({}, notificationsFilter), {
            organizationUID: activeProfileType === Account.constants.PROFILE_TYPES.HCP ? activeOrganizationUID : null
        }), isNil));
        const requestUrl = `/api/InstantNotification/filter?${query}`;
        const notifications = yield call(ApiService.regionalRequest, requestUrl);
        const notificationsRecords = get(notifications, 'records', []);
        const isFetchEnrollingSharingRequestsInProgress = yield select(HcpSelectors.isFetchEnrollingSharingRequestsInProgress);
        if (isFetchEnrollingSharingRequestsInProgress) {
            yield take([
                HcpActionTypes.FETCH_ENROLLING_SHARING_REQUESTS_SUCCESS,
                HcpActionTypes.FETCH_ENROLLING_SHARING_REQUESTS_ERROR
            ]);
        }
        const ctx = {
            hcpClinicMemberships: yield select(Account.selectors.hcpClinicMemberships),
            passphrase: yield select(Account.selectors.passphrase),
            prvKeyPem: yield select(Account.selectors.prvKeyPem),
            enrollingSharingRequests: yield select(HcpSelectors.enrollingSharingRequests),
            activeOrganizationUID: yield select(Account.selectors.activeOrganizationUID),
            notificationResourcesToLinks: yield call(mapNotificationResourcesToLinks, notificationsRecords)
        };
        const transformedNotificationsRecords = yield all(notificationsRecords.map((notificationRecord)=>transformNotificationsRecord(notificationRecord, ctx)));
        yield put(actions.fetchListSuccess(_object_spread_props(_object_spread({}, notifications), {
            records: transformedNotificationsRecords
        })));
    } catch (err) {
        yield put(actions.fetchListError(err));
        yield call(App.dispatchError, err, null, alert);
    }
}
function* newInstantNotification({ payload }) {
    const { notification, identityUID, organizationUID } = payload;
    yield put(actions.refreshOnNewInstantNotification(notification, identityUID, organizationUID));
    yield take([
        actionTypes.REFRESH_ON_NEW_INSTANT_NOTIFICATION_SUCCESS
    ]);
    const ctx = {
        hcpClinicMemberships: yield select(Account.selectors.hcpClinicMemberships),
        passphrase: yield select(Account.selectors.passphrase),
        prvKeyPem: yield select(Account.selectors.prvKeyPem),
        enrollingSharingRequests: yield select(HcpSelectors.enrollingSharingRequests),
        activeOrganizationUID: yield select(Account.selectors.activeOrganizationUID),
        notificationResourcesToLinks: yield call(mapNotificationResourcesToLinks, [
            notification
        ])
    };
    const transformedNotification = yield call(transformNotificationsRecord, notification, ctx);
    const activeOrganizationUID = yield select(Account.selectors.activeOrganizationUID);
    // for PWD and Caregiver
    if (!organizationUID || !activeOrganizationUID) {
        yield put(actions.addNewNotification(transformedNotification));
        return;
    }
    // for HCP
    const isNotificationForCurrentActiveOrganization = organizationUID === activeOrganizationUID;
    if (isNotificationForCurrentActiveOrganization) {
        yield put(actions.addNewNotification(transformedNotification));
    } else {
        const hcpClinicMemberships = yield select(Account.selectors.hcpClinicMemberships);
        const hcpClinicMembership = find(hcpClinicMemberships, ({ clinic })=>clinic.organizationUID === organizationUID);
        const { clinicHcpMembershipId } = hcpClinicMembership || {};
        if (clinicHcpMembershipId) {
            yield put(Account.actions.setClinicMembershipHasActiveNotifications(clinicHcpMembershipId, true));
        }
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* getNotificationUpdateQueryParam() {
    const notificationsFilter = yield select(selectors.notificationsFilter);
    const notifications = yield select(selectors.notificationsRecords);
    if (!notifications || !notifications.length) {
        return null;
    }
    const activeOrganizationUID = yield select(Account.selectors.activeOrganizationUID);
    return _object_spread_props(_object_spread({}, notificationsFilter), {
        fromTimestamp: minBy(notifications, 'timestamp').timestamp,
        toTimestamp: maxBy(notifications, 'timestamp').timestamp,
        organizationUID: activeOrganizationUID
    });
}
//----------------------------------------------------------------------------------------------------------------------
function* dismissNotification({ payload }) {
    const { notification } = payload;
    try {
        const { instantNotificationId } = notification;
        yield delay(300); // Wait for animation
        const requestUrl = `/api/InstantNotification/${instantNotificationId}/dismiss`;
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: {
                instantNotificationId
            }
        });
        yield put(actions.dismissNotificationSuccess(notification));
    } catch (err) {
        yield put(actions.dismissNotificationError(err, notification));
        yield call(App.dispatchError, err);
    }
}
function* waitForDismissFilteredNotificationsConfirmation() {
    yield put(actions.dismissFilteredNotificationOpenConfirmation());
    return yield take([
        actionTypes.DISMISS_FILTERED_NOTIFICATIONS_CONFIRM,
        actionTypes.DISMISS_FILTERED_NOTIFICATIONS_CANCEL
    ]);
}
function* dismissFilteredNotifications() {
    try {
        const params = yield getNotificationUpdateQueryParam();
        if (!params) {
            yield put(actions.dismissFilteredNotificationSuccess());
            return;
        }
        const confirmationResult = yield waitForDismissFilteredNotificationsConfirmation();
        if (confirmationResult.type === actionTypes.DISMISS_FILTERED_NOTIFICATIONS_CANCEL) {
            yield put(actions.dismissFilteredNotificationCancel());
            return;
        }
        const requestUrl = '/api/InstantNotification/dismissAll';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'PUT',
            body: _object_spread({}, params)
        });
        yield put(actions.fetchList());
        const fetchListResult = yield take([
            actionTypes.FETCH_LIST_SUCCESS,
            actionTypes.FETCH_LIST_ERROR
        ]);
        if (fetchListResult.type === actionTypes.FETCH_LIST_SUCCESS) {
            yield put(actions.dismissFilteredNotificationSuccess());
        } else {
            yield put(actions.dismissFilteredNotificationError(fetchListResult.err));
        }
    } catch (err) {
        yield put(actions.dismissFilteredNotificationError(err));
        yield call(App.dispatchError, err);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* markNotificationsRead() {
    try {
        const params = yield getNotificationUpdateQueryParam();
        if (!params) {
            yield put(actions.markNotificationsReadSuccess());
            return;
        }
        const requestUrl = '/api/InstantNotification/readAll';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'PUT',
            body: _object_spread({}, params)
        });
        yield put(actions.fetchList());
        const fetchListResult = yield take([
            actionTypes.FETCH_LIST_SUCCESS,
            actionTypes.FETCH_LIST_ERROR
        ]);
        if (fetchListResult.type === actionTypes.FETCH_LIST_SUCCESS) {
            yield put(actions.markNotificationsReadSuccess());
        } else {
            yield put(actions.markNotificationsReadError(fetchListResult.err));
        }
    } catch (err) {
        yield put(actions.markNotificationsReadError(err));
        yield call(App.dispatchError, err);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* sendClinicPatientNewReadingsAddedNotification({ payload }) {
    try {
        yield call(NotificationsService.sendClinicPatientNewReadingsAddedNotification, payload);
        yield put(actions.sendNotificationSuccess());
    } catch (err) {
        yield put(actions.sendNotificationError(err));
    }
}
function* sagas() {
    yield takeLatest(actionTypes.FETCH_LIST, fetchList);
    yield takeLatest(appActionTypes.NEW_INSTANT_NOTIFICATION, newInstantNotification);
    yield takeEvery(actionTypes.DISMISS_NOTIFICATION, dismissNotification);
    yield takeEvery(actionTypes.DISMISS_FILTERED_NOTIFICATIONS, dismissFilteredNotifications);
    yield takeLatest(actionTypes.MARK_NOTIFICATIONS_READ, markNotificationsRead);
    // eslint-disable-next-line max-len
    yield takeEvery(actionTypes.SEND_CLINIC_PATIENT_NEW_READINGS_ADDED_NOTIFICATION, sendClinicPatientNewReadingsAddedNotification);
}
export default [
    sagas
];
